/**
 * Logout route — clears session and redirects.
 *
 * Consuming apps redirect here to sign out:
 *   https://login.abacus.dev/logout?redirect_uri=https://categorizer.abacus.dev
 *
 * Flow:
 *   1. Browser navigates to /logout (same domain as session cookie)
 *   2. Better Auth signOut() clears the session
 *   3. Redirects to redirect_uri or /login
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { z } from "zod";

export const Route = createFileRoute("/logout")({
  validateSearch: z.object({
    redirect_uri: z.string().optional(),
  }).parse,
  component: LogoutComponent,
});

function LogoutComponent() {
  const { redirect_uri } = Route.useSearch();
  const navigate = useNavigate();
  const { signOut, isAuthenticated, isLoading } = useAuth();
  const signedOut = useRef(false);

  useEffect(() => {
    if (isLoading || signedOut.current) return;
    signedOut.current = true;

    const doLogout = async () => {
      if (isAuthenticated) {
        await signOut();
      }
      // Always go to login — never redirect back to the calling app directly,
      // as it would detect no session and redirect back here (infinite loop).
      // Pass redirect_uri to login so after re-authentication the user lands
      // at the right app.
      navigate({
        to: "/login",
        search: redirect_uri ? { redirect_uri } : {},
      });
    };

    doLogout();
  }, [isLoading, isAuthenticated, signOut, redirect_uri, navigate]);

  return (
    <div
      className="flex flex-col items-center justify-center py-12 gap-3"
      role="status"
      data-testid="logout-loading"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Signing you out…</p>
    </div>
  );
}
